import useApplicationUpdate from '@UI/Hooks/useApplicationUpdate';
import captureInProgressSignal from '@Guidelines/Shell/Signals/captureInProgressSignal';
import { css } from '@emotion/react';
import { RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Button, Message } from '@verclam/ui-kit';

const messageStyle = css`
	margin: 0 auto;
	display: flex;
	width: fit-content;
`;

const contentStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const textStyle = css`
	flex: 1 1 auto;
`;

const actionsStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const actionButtonStyle = css`
	min-height: 44px;
`;

/**
 * Masked while no update is waiting, while a capture is in progress (here or in another tab), or
 * once the user has postponed it — visible otherwise, and replaced by an "applying" state
 * (disabled, spinning button) the moment "Recharger" is pressed. The automatic reload that follows
 * an applied update is skipped while a capture is in progress, so a recording is never destroyed.
 *
 * "Plus tard" is postponed for the current session only, never persisted: every version raises a
 * fresh update, so a postponement has no natural expiry, and this banner is the only path through
 * which an update ever applies — a durable dismissal would strand the application on its installed
 * version indefinitely.
 */
const ApplicationUpdateBanner: React.FC = () => {
	const reloadPage = React.useCallback((): void => {
		if (captureInProgressSignal.peek()) {
			return;
		}
		window.location.reload();
	}, []);
	const { isUpdateAvailable, applyUpdate } = useApplicationUpdate({ reloadPage });
	const isSuppressed = useSignalValue(captureInProgressSignal);
	const [isPostponed, setIsPostponed] = React.useState<boolean>(false);
	const [isApplying, setIsApplying] = React.useState<boolean>(false);

	const handleApply = React.useCallback((): void => {
		setIsApplying(true);
		applyUpdate();
	}, [applyUpdate]);

	const handlePostpone = React.useCallback((): void => {
		setIsPostponed(true);
	}, []);

	const isVisible = isUpdateAvailable && !isSuppressed && !isPostponed;

	if (!isVisible) {
		return null;
	}

	return (
		<Message
			severity="info"
			css={messageStyle}
			content={
				<div css={contentStyle}>
					<RefreshCw size={20} aria-hidden="true" />
					<span css={textStyle}>Une nouvelle version est disponible</span>
					<div css={actionsStyle}>
						<Button
							type="button"
							label="Recharger"
							loading={isApplying}
							css={actionButtonStyle}
							onClick={handleApply}
						/>
						<Button
							type="button"
							label="Plus tard"
							severity="secondary"
							disabled={isApplying}
							css={actionButtonStyle}
							onClick={handlePostpone}
						/>
					</div>
				</div>
			}
		/>
	);
};

export default ApplicationUpdateBanner;
